import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Generates a username from first and last name
 * @param {string} firstName 
 * @param {string} lastName 
 * @returns {string} Generated username like "john-smith"
 */
export const generateUsername = (firstName, lastName) => {
  if (!firstName && !lastName) return '';
  
  const first = (firstName || '').toLowerCase().trim();
  const last = (lastName || '').toLowerCase().trim();
  
  // Remove special characters and spaces, keep only letters and numbers
  const cleanFirst = first.replace(/[^a-z0-9]/g, '');
  const cleanLast = last.replace(/[^a-z0-9]/g, '');
  
  if (!cleanFirst && !cleanLast) return '';
  
  if (cleanFirst && cleanLast) {
    return `${cleanFirst}-${cleanLast}`;
  } else {
    return cleanFirst || cleanLast;
  }
};

/**
 * Checks if a username is already taken
 * @param {string} username 
 * @returns {Promise<boolean>} True if username is available
 */
export const isUsernameAvailable = async (username) => {
  if (!username) return false;
  
  try {
    const q = query(
      collection(db, 'users'),
      where('username', '==', username)
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.empty; // True if no documents found (username available)
  } catch (error) {
    console.error('Error checking username availability:', error);
    return false;
  }
};

/**
 * Generates a unique username by adding numbers if needed
 * @param {string} firstName 
 * @param {string} lastName 
 * @returns {Promise<string>} Unique username
 */
export const generateUniqueUsername = async (firstName, lastName) => {
  const baseUsername = generateUsername(firstName, lastName);
  
  if (!baseUsername) {
    // Fallback to random string if names can't generate username
    return `user-${Math.random().toString(36).substring(2, 8)}`;
  }
  
  // Check if base username is available
  if (await isUsernameAvailable(baseUsername)) {
    return baseUsername;
  }
  
  // If not available, try with numbers
  for (let i = 1; i <= 99; i++) {
    const candidateUsername = `${baseUsername}-${i}`;
    if (await isUsernameAvailable(candidateUsername)) {
      return candidateUsername;
    }
  }
  
  // Fallback to random if all numbered versions are taken
  return `${baseUsername}-${Math.random().toString(36).substring(2, 8)}`;
};

/**
 * Validates username format
 * @param {string} username 
 * @returns {boolean} True if username is valid format
 */
export const isValidUsername = (username) => {
  if (!username) return false;
  
  // Must be 3-30 characters, letters, numbers, hyphens only
  const usernameRegex = /^[a-z0-9-]{3,30}$/;
  
  // Can't start or end with hyphen
  if (username.startsWith('-') || username.endsWith('-')) {
    return false;
  }
  
  // Can't have consecutive hyphens
  if (username.includes('--')) {
    return false;
  }
  
  return usernameRegex.test(username);
};

/**
 * Gets user by username
 * @param {string} username 
 * @returns {Promise<Object|null>} User document or null
 */
export const getUserByUsername = async (username) => {
  if (!username) return null;
  
  try {
    const q = query(
      collection(db, 'users'),
      where('username', '==', username)
    );
    
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }
    
    const doc = querySnapshot.docs[0];
    return {
      id: doc.id,
      ...doc.data()
    };
  } catch (error) {
    console.error('Error getting user by username:', error);
    return null;
  }
};